<template>
  <div>
    <b-overlay
      :show="usersResponses('usersList') && usersEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Users
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Users</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-header class="pb-0">
          <!-- filters -->
          <b-form
            @submit.stop.prevent
            class="w-100">
            <b-row>
              <b-col xl="4">
                <b-row>
                  <b-col
                    sm="8"
                    lg="9"
                    xl="8">
                    <label
                      for="filter-user"
                      class="d-flex align-items-center">
                      <span class="font-weight-bold">User ID</span>
                      <i
                        v-b-tooltip.hover.right
                        title="Must be a valid UUID identifier"
                        class="fas fa-question-circle fa-sm ml-3"
                        :class="{ 'text-danger opacity-85' : !isValidUUID }" />
                    </label>
                    <b-form-input
                      id="filter-user"
                      v-model="localGetParams.code"
                      type="text"
                      placeholder="b89eef98-838c-426a-af14-1a0cbe8a2b81"
                      pattern="^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$"
                      :state="!isValidUUID ? false : null"
                      class=""
                      @input="validateUserId" />
                  </b-col>
                  <b-col
                    sm="4"
                    lg="3"
                    xl="4">
                    <b-form-group
                      label="Reseller ID"
                      label-for="filter-reseller"
                      class="mb-8">
                      <b-form-input
                        id="filter-reseller"
                        v-model.number="localGetParams.reseller"
                        type="number"
                        placeholder="15"
                        class="" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col xl="6">
                <b-row>
                  <b-col
                    sm="6"
                    xl="4">
                    <b-form-group
                      label="Created"
                      label-for="filter-created"
                      class="mb-8 daterange-form-group">
                      <date-range-picker
                        v-model="createdRange"
                        auto-apply
                        :locale-data="{ firstDay: 1, format: 'DD.MM.YYYY' }"
                        :ranges="false"
                        class="w-100">
                        <template v-slot:input="picker">
                          <span v-if="rangeIsSelected('createdRange')">
                            {{ picker.startDate | moment("DD.MM.YYYY") }} - {{ picker.endDate | moment("DD.MM.YYYY") }}
                          </span>
                          <span
                            v-else
                            class="text-muted no-select">Select date range</span>
                        </template>
                      </date-range-picker>
                    </b-form-group>
                  </b-col>
                  <b-col
                    sm="6"
                    xl="4">
                    <b-form-group
                      label="Expiry"
                      label-for="filter-expiry"
                      class="mb-8 daterange-form-group">
                      <date-range-picker
                        v-model="expiryRange"
                        auto-apply
                        :locale-data="{ firstDay: 1, format: 'DD.MM.YYYY' }"
                        :ranges="false"
                        class="w-100">
                        <template v-slot:input="picker">
                          <span v-if="rangeIsSelected('expiryRange')">
                            {{ picker.startDate | moment("DD.MM.YYYY") }} - {{ picker.endDate | moment("DD.MM.YYYY") }}
                          </span>
                          <span
                            v-else
                            class="text-muted no-select">Select date range</span>
                        </template>
                      </date-range-picker>
                    </b-form-group>
                  </b-col>
                  <b-col
                    sm="4"
                    xl="3">
                    <b-form-group
                      label="Status"
                      label-for="filter-status"
                      class="mb-8">
                      <b-form-select
                        :value="localGetParams.status"
                        value-field="key"
                        @change="
                          setLocalGetParam($event, 'status')
                          setLocalGetParam(1, 'page')
                        "
                        class="">
                        <template #first>
                          <b-form-select-option :value="null">
                            All Statuses
                          </b-form-select-option>
                        </template>
                        <b-form-select-option
                          disabled
                          :value="null">
                          ──────────
                        </b-form-select-option>
                        <b-form-select-option
                          v-for="status in usersStatuses"
                          :key="`status-${status._id}`"
                          :value="status.key">
                          {{ status.name }}
                        </b-form-select-option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                xl="2"
                class="d-flex justify-content-end align-items-end pb-8">
                <b-button
                  type="reset"
                  v-wave
                  variant="light"
                  @click="clearLocalFilters">
                  Clear
                </b-button>
                <b-button
                  type="submit"
                  v-wave
                  variant="primary"
                  class="ml-2"
                  @click="applyLocalFilters">
                  <span>Apply</span>
                  <i class="fas fa-filter fa-sm pr-0 ml-2" />
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-card-header>

        <b-card-body>

          <b-table
            :items="usersList"
            :fields="visibleFields"
            :class="{ 'table-hover' : !usersEmpty }"
            stacked="md"
            head-variant=""
            show-empty
            responsive>
            
            <!-- custom headings -->
            <template #head(code)="data">
              <sort-button
                :data="data"
                :getParams="usersGetParams"
                class="ml-2"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(item_id)="data">
              <sort-button
                :data="data"
                :getParams="usersGetParams"
                class="ml-2"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(devices)="data">
              <sort-button
                :data="data"
                :getParams="usersGetParams"
                align="right"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(created)="data">
              <sort-button
                :data="data"
                :getParams="usersGetParams"
                @change="key => handleSortDirection(key)" />
            </template>
            <template #head(expiry)="data">
              <sort-button
                :data="data"
                :getParams="usersGetParams"
                @change="key => handleSortDirection(key)" />
            </template>

            <!-- custom cells -->
            <template #cell(code)="data">
              <b-button
                v-wave
                size="sm"
                variant="hover-light-info"
                class="font-weight-normal py-1 px-2"
                @click="copyInput(`#copy-user-code-${data.item._id}`)">
                {{ data.item.code }}
                <i class="far fa-copy fa-sm ml-2 p-0" />
              </b-button>
              <b-input
                :id="`copy-user-code-${data.item._id}`"
                :value="data.item.code"
                class="position-absolute"
                style="opacity: 0.01; z-index: -1000; left: -9999px;" />
            </template>
            <template #cell(item_id)="data">
              <b-button
                size="sm"
                variant="hover-light-info"
                class="font-weight-normal py-1 px-2"
                @click="filterByResellerId(data.item.item_id)">
                {{ data.item.item_id }}
                <i class="fas fa-filter fa-xs ml-2 p-0" />
              </b-button>
            </template>
            <template #cell(devices)="data">
              <span>
                {{ data.item.devices | commaValue }}
              </span>
            </template>
            <template #cell(status)="data">
              <div class="d-flex justify-center-start justify-content-md-center align-items-center">
                <!-- Wrapper for fake element + checkbox -->
                <div
                  class="position-relative"
                  style="width: fit-content;">
                  <!-- Fake element above checkbox that TRIGGERS event -->
                  <div
                    class="w-100 h-100 position-absolute"
                    style="z-index: 20;"
                    role="button"
                    @click="handleStatusChange(data.item)" />
                  <!-- Checkbox that REPRESENTS current status, not changes it -->
                  <b-form-checkbox
                    :checked="data.item.status === 'on'"
                    size="lg"
                    switch
                    name="switch-vpn-server"
                    class="switch-success ml-0 ml-md-3 mb-1 mb-md-2" />
                </div>
              </div>
              <div
                v-if="data.item.suspended_auto === 'on'"
                class="font-size-xs text-muted mt-1">
                auto suspended
              </div>
              <div
                v-if="data.item.suspended_by_admin"
                class="font-size-xs text-muted mt-1 d-flex align-items-center justify-content-center text-center">
                <span>suspended by admin</span>
                <i
                  v-if="data.item.suspended_admin_memo"
                  v-b-tooltip.hover
                  :title="data.item.suspended_admin_memo"
                  class="fas fa-question-circle fa-sm ml-2" />
              </div>
            </template>
            <template #cell(created)="data">
              <span>{{ data.item.created | moment("DD.MM.YYYY, HH:mm") }}</span>
            </template>
            <template #cell(expiry)="data">
              <span>{{ data.item.expiry | moment("DD.MM.YYYY, HH:mm") }}</span>
              <i
                class="label label-dot mx-4"
                :class="{
                  'label-danger opacity-80': userExpiryStatus(data.item._id) === 'expired',
                  'label-warning opacity-80': userExpiryStatus(data.item._id) === 'tomorrow',
                  'label-dark opacity-35': userExpiryStatus(data.item._id) === 'stable'
                }" />
              <span class="font-weight-bold font-size-sm text-dark opacity-50">{{ data.item.expiry | daysLeft }}</span>
            </template>
            <template #cell(extras)="data">
              <div class="table-grid-items">
                <!-- revenuecat_appuserid -->
                <div v-if="userHasRevenuecatId(data.item._id)">
                  <span
                    :id="`revenuecat_appuserid-${data.item._id}`"
                    :disabled="!userHasRevenuecatId(data.item._id)"
                    class="svg-icon svg-icon-sm"
                    @click="
                      userHasRevenuecatId(data.item._id)
                        ? copyInput(`#copy-revenuecat_appuserid-${data.item._id}`)
                        : false">
                    <inline-svg
                      src="media/svg/icons/Integrations/revenuecat.svg"
                      class="svg-icon m-0 cursor-pointer" />
                  </span>
                  <b-tooltip :target="`revenuecat_appuserid-${data.item._id}`">
                    <div class="mb-2 font-weight-bold line-height-sm no-select">
                      Revenuecat ID
                    </div>
                    <div class="font-size-xs mb-3 px-2 py-1 rounded border bg-light">
                      {{ data.item.revenuecat_appuserid }}
                    </div>
                    <div class="font-size-sm opacity-75 no-select">(click icon to copy)</div>
                  </b-tooltip>
                  <b-input
                    :id="`copy-revenuecat_appuserid-${data.item._id}`"
                    :value="data.item.revenuecat_appuserid"
                    class="position-absolute"
                    style="opacity: 0.01; z-index: -1000; left: -9999px;" />
                </div>
              </div>
            </template>
            <template #cell(edit)="data">
              <b-dropdown
                no-caret
                :boundary="usersListLength < 5 ? 'window' : 'scrollParent'"
                right
                variant="outline-primary"
                toggle-class="btn-icon"
                size="sm"
                class="mr-4 dropdown-inline">
                <template v-slot:button-content>
                  <i class="fa fa-ellipsis-h fa-sm"></i>
                </template>
                <b-dropdown-item-button
                  :disabled="usersResponses('getUser') || usersResponses('changeUserDevices')"
                  @click="showModal('changeDevices', data.item._id)">
                  <i class="fa fa-tablet-alt fa-sm mr-1"></i>
                  Change devices limit
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="usersResponses('getUser') || usersResponses('changeUserExpiry')"
                  @click="showModal('changeExpiration', data.item._id)">
                  <i class="fa fa-calendar-alt fa-sm mr-1"></i>
                  Change expiration date
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="usersResponses('getUser') || usersResponses('getUserServers')"
                  @click="showModal('userServers', data.item._id)">
                  <i class="fa fa-server fa-sm mr-1"></i>
                  Show servers list
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="usersResponses('getUser') || usersResponses('getUserServers')"
                  @click="showModal('downloadConfig', data.item._id)">
                  <i class="fa fa-shield-alt fa-sm mr-1"></i>
                  Download VPN config
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="usersResponses('getUser') || usersResponses('getUserToken')"
                  @click="showModal('userToken', data.item._id)">
                  <i class="fa fa-key fa-sm mr-1"></i>
                  Show access token
                </b-dropdown-item-button>
                <b-dropdown-item-button disabled>
                  <i class="fa fa-bell fa-sm mr-1"></i>
                  Manage notifications
                </b-dropdown-item-button>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item-button
                  variant="danger"
                  :disabled="usersResponses('resetUserSessions')"
                  @click="showModal('resetSessionsConfirm', data.item._id)">
                  <i class="fa fa-sign-out-alt fa-sm mr-1 text-danger opacity-75"></i>
                  Reset current sessions
                </b-dropdown-item-button>
              </b-dropdown>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                @click="
                  selectUser(data.item),
                  showDeletePopover(data.item._id)
                ">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>

            <!-- empty state -->
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>

          <div class="w-100 d-flex align-items-center flex-wrap pt-6">
            <b-dropdown
              variant="hover-light-primary"
              id="custiomize-table"
              no-caret
              size="sm"
              class="mr-auto">
              <template #button-content>
                <span>Customize Table</span>
                <i class="fa fa-cog fa-sm pl-2 pr-0"></i>
              </template>
              <div class="d-flex flex-column px-3">
                <b-form-checkbox
                  v-for="field in fields"
                  :key="`dropdown-item-${field.key}`"
                  v-model="field.visible"
                  :disabled="field.default"
                  size="md"
                  class="my-1">
                  {{ field.label }}
                </b-form-checkbox>
              </div>
            </b-dropdown>
            <div class="d-flex align-items-center">
              <div class="d-flex align-items-center mr-2">
                <span class="font-weight-bold font-size-sm">Per Page:</span>
                <b-form-select
                  size="sm"
                  :value="usersLimit"
                  :options="limits"
                  class="w-auto ml-2"
                  @change="
                    setUsersGetParam(1, 'page'),
                    setUsersGetParam($event, 'limit')
                  " />
              </div>
              <!--
                Don't remove v-if statement!
                https://github.com/bootstrap-vue/bootstrap-vue/issues/3372#issuecomment-596158197
              -->
              <b-pagination
                v-if="!usersEmpty"
                :value="usersPage"
                :total-rows="totalUsers"
                :per-page="usersLimit"
                size="md"
                first-number
                last-number
                class="mb-0 ml-auto"
                @change="setUsersGetParam($event, 'page')">
                <template #prev-text>
                  <i class="fa fa-xs fa-chevron-left"></i>
                </template>
                <template #next-text>
                  <i class="fa fa-xs fa-chevron-right"></i>
                </template>
              </b-pagination>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>

    <modal-suspend-user />
    <modal-change-devices />
    <modal-change-expiration />
    <modal-user-token />
    <modal-reset-sessions />
    <modal-download-config />
    <modal-user-servers />
    
  </div>
</template>

<script>
import moment from 'moment'
import {
  // actions
  GET_USERS,
  GET_USER,
  SWITCH_USER,
  GET_USER_TOKEN,
  GET_USER_SERVERS,
  GET_USER_VPN_SERVERS,

  // mutations
  CLEAR_USERS,
  SET_USERS_GET_PARAMS,
  SET_USERS_GET_PARAM,
  CLEAR_USERS_GET_PARAMS,
  SELECT_USER,

  // constans
  DEFAULT_USERS_GET_PARAMS,
  DEFAULT_USERS_ROUTER_PARAMS,
  DEFAULT_USERS_LOCAL_GET_PARAMS,
  DEFAULT_RANGE_PARAMS,
} from '@/core/services/store/users.module'
import { GET_COUNTRIES } from '@/core/services/store/countries.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { daysLeft, commaValue } from '@/core/filters'
import { regex_uuid, regex_digits } from '@/core/regex'
import {
  showToast,
  copyInput,
  showDeletePopover,
  hideDeletePopover,
  restoreTableColumns
} from '@/core/utils'
import SortButton from '@/view/layout/controls/SortButton.vue'
import ModalSuspendUser from './components/ModalSuspendUser.vue'
import ModalChangeDevices from './components/ModalChangeDevices.vue'
import ModalChangeExpiration from './components/ModalChangeExpiration.vue'
import ModalUserToken from './components/ModalUserToken.vue'
import ModalResetSessions from './components/ModalResetSessions.vue'
import ModalDownloadConfig from './components/ModalDownloadConfig.vue'
import ModalUserServers from './components/ModalUserServers.vue'

const _ = require('lodash')

export default {
  name: 'UsersList',
  filters: {
    daysLeft,
    commaValue
  },
  components: {
    SortButton,
    ModalSuspendUser,
    ModalChangeDevices,
    ModalChangeExpiration,
    ModalUserToken,
    ModalResetSessions,
    ModalDownloadConfig,
    ModalUserServers,
  },
  data() {
    return {
      fields: [
        {
          label: 'User ID',
          key: 'code',
          class: ['align-middle', 'font-size-sm', 'text-nowrap', 'pl-3 py-md-0'],
          visible: true,
          default: true,
          thStyle: 'width: 25%;',
        },
        {
          label: 'Reseller ID',
          key: 'item_id',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
        },
        {
          label: 'Devices',
          key: 'devices',
          class: ['align-middle', 'text-nowrap', 'text-left text-md-right'],
          visible: true,
        },
        {
          label: 'Status',
          key: 'status',
          class: ['align-middle', 'text-nowrap', 'text-center', 'px-0 px-md-6 px-lg-12'],
          visible: true,
        },
        {
          label: 'Created',
          key: 'created',
          class: ['align-middle', 'text-nowrap', 'pr-8'],
          visible: true,
        },
        {
          label: 'Expiry',
          key: 'expiry',
          class: ['align-middle', 'text-nowrap', 'pr-8'],
          visible: true,
        },
        {
          label: 'Extras',
          key: 'extras',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5'],
          visible: true,
          default: true,
        }
      ],
      limits: [10, 25, 50, 100],
      usersStatuses: [
        { _id: 'active', name: 'Active', key: 'on' },
        { _id: 'disabled', name: 'Disabled', key: 'off' }
      ],
      localGetParams: { ...DEFAULT_USERS_LOCAL_GET_PARAMS },
      createdRange: { ...DEFAULT_RANGE_PARAMS },
      expiryRange: { ...DEFAULT_RANGE_PARAMS },
      sortClickCount: 0,
      isValidUUID: true,

      showToast,
      copyInput,
      showDeletePopover,
      hideDeletePopover,
      restoreTableColumns,
    }
  },
  computed: {
    ...mapGetters([
      'usersList',
      'usersListLength',
      'usersEmpty',
      'usersGetParams',
      'usersPage',
      'usersLimit',
      'totalUsers',
      'totalUserPages',
      'selectedUser',
      'selectedUserId',
      'userExpiryStatus',
      'userHasRevenuecatId',
      'usersResponses',
    ]),

    visibleFields() {
      return this.fields.filter(field => field.visible)
    }
  },
  watch: {
    usersGetParams: {
      handler: function(newVal) {
        // sanitize null values
        const cleanedQuery = { ...newVal }
        Object.keys(cleanedQuery).forEach(key => {
          if (!cleanedQuery[key] || cleanedQuery[key] == 'null') {
            delete cleanedQuery[key]
          }
        })
        this.$router.push({
          name: this.$options.name,
          query: cleanedQuery
        }).catch(() => {})
      },
      deep: true
    },

    $route() {
      this.handleUrl()
    },

    visibleFields() {
      localStorage.setItem('userTableColumns', JSON.stringify(this.fields))
    },

    usersEmpty(newVal) {
      // this function forces to the last available page
      // if user manually choosed page that doesn't exist
      if (
        newVal &&
        this.usersPage > this.totalUserPages &&
        this.totalUserPages > 1) {
        this.setUsersGetParam(this.totalUserPages, 'page')
        this.$router.push({
          name: this.$options.name,
          query: { ...this.$route.query, page: this.totalUserPages }
        }).catch(() => {})
        this.getUsers()
      }
    },
  },
  created() {
    this.$store.dispatch(GET_COUNTRIES)
    this.clearUsers()
    this.restoreTableColumns('usersTableColumns')
    this.handleUrl()
    this.copyToLocalGetParams()

    eventBus.$on('showToast/UsersList', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    eventBus.$off('showToast/UsersList')

    this.$store.commit(CLEAR_USERS_GET_PARAMS)
  },
  methods: {
    validateQueryParams(queryParams) {
      const allowedKeys = Object.keys(DEFAULT_USERS_GET_PARAMS)

      // Check if there are no extra query parameters
      const extraKeys = Object.keys(queryParams).filter(
        key => !allowedKeys.includes(key)
      )

      // Validate each query parameter
      const validLimit = this.limits.includes(Number(queryParams.limit))
      const validPage = regex_digits.test(queryParams.page)
      const validSort = [
        'code',
        'item_id',
        'devices',
        'created',
        'expiry'
      ].includes(queryParams.sort)
      const validOrd = ['asc', 'desc'].includes(queryParams.ord)

      let validStatus = true
      if (queryParams.status) {
        validStatus = ['on', 'off'].includes(queryParams.status)
      }

      return (
        extraKeys.length === 0 &&
        validLimit &&
        validPage &&
        validSort &&
        validOrd &&
        validStatus
      )
    },

    handleUrl() {
      const queryParams = this.$route.query
      const valid = this.validateQueryParams(queryParams)
      const isDefaultQuery = _.isEqual(DEFAULT_USERS_ROUTER_PARAMS, queryParams)

      if (valid) {
        if (isDefaultQuery) {
          this.clearLocalFilters()
        } else {
          Object.keys(this.localGetParams).forEach(key => {
            this.setLocalGetParam(queryParams[key] ? queryParams[key] : null, key)
          })
        }

        this.applyUrlQueries()
      } else {
        this.clearUrlQueries()
      }

      this.getUsers()
    },

    applyUrlQueries() {
      this.$store.commit(SET_USERS_GET_PARAMS, this.$route.query)
    },

    clearUrlQueries() {
      this.$store.commit(CLEAR_USERS_GET_PARAMS)
      this.$router.push({
        name: this.$options.name,
        query: {}
      }).catch(() => {})
    },

    copyDateRange(startDateKey, endDateKey, targetRange) {
      if (this.usersGetParams[startDateKey] && this.usersGetParams[endDateKey]) {
        targetRange.startDate = new Date(this.usersGetParams[startDateKey])
        targetRange.endDate = new Date(this.usersGetParams[endDateKey])
      }
    },

    copyToLocalGetParams() {
      Object.keys(this.localGetParams).forEach(key => {
        this.localGetParams[key] = this.usersGetParams[key]
        this.copyDateRange('from', 'to', this.createdRange)
        this.copyDateRange('expire-from', 'expire-to', this.expiryRange)
      })
    },

    getUsers() {
      this.$store
        .dispatch(GET_USERS)
        .catch(err => {
          const error = err.response.data
          if (!error.errors || !error.errors[0]) {
            return
          }
          const error_code = error.errors[0].code
          if (error_code === 'code_invalid') {
            this.clearUrlQueries()
            this.localGetParams.code = null
            this.isValidUUID = true
          }
        })
    },

    clearUsers() {
      this.$store.commit(CLEAR_USERS)
    },

    applyLocalFilters() {
      const format = 'YYYY-MM-DD'

      const ranges = {
        created: {
          start: this.createdRange.startDate,
          end: this.createdRange.endDate,
          fromKey: 'from',
          toKey: 'to',
        },
        expiry: {
          start: this.expiryRange.startDate,
          end: this.expiryRange.endDate,
          fromKey: 'expire-from',
          toKey: 'expire-to',
        },
      }

      Object.keys(ranges).forEach((rangeType) => {
        const range = ranges[rangeType]
        if (range.start && range.end) {
          const startDate = moment(range.start).format(format)
          const endDate = moment(range.end).format(format)
          this.localGetParams[range.fromKey] = startDate
          this.localGetParams[range.toKey] = endDate
        }
      })

      Object.entries(this.localGetParams).forEach(([key, value]) => {
        this.setUsersGetParam(value, key)
      })
    },

    clearLocalFilters() {
      this.localGetParams = { ...DEFAULT_USERS_LOCAL_GET_PARAMS }
      this.createdRange = { ...DEFAULT_RANGE_PARAMS }
      this.expiryRange = { ...DEFAULT_RANGE_PARAMS }
      this.applyLocalFilters()
    },

    setUsersGetParam(value, param) {
      this.$store.commit(SET_USERS_GET_PARAM, { value, param })
    },

    setLocalGetParam(value, param) {
      this.localGetParams[param] = value
    },

    handleSortDirection(key) {
      if (key === this.usersGetParams.sort) {
        if (this.sortClickCount === 2) {
          // The user has clicked the same key three times, so force default sorting.
          this.setUsersGetParam('desc', 'ord')
          this.setUsersGetParam('created', 'sort') // Default sort by "created" and "desc".
          this.sortClickCount = 0 // Reset the click count.
        } else {
          // Toggle between 'asc' and 'desc'.
          this.setUsersGetParam(
            this.usersGetParams.ord === 'desc' ? 'asc' : 'desc',
            'ord'
          )
          this.sortClickCount++
        }
      } else {
        // The user clicked a different key, reset the click count and update the sorting key.
        this.sortClickCount = 1
        this.setUsersGetParam('asc', 'ord')
        this.setUsersGetParam(key, 'sort')
      }
    },

    dataIsReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    filterByResellerId(id) {
      this.localGetParams.reseller = id
      this.applyLocalFilters()
    },

    rangeIsSelected(type) {
      return this[type].startDate !== null
    },

    validateUserId() {
      if (
        this.localGetParams.code
        && this.localGetParams.code.length > 0) {
        this.isValidUUID = regex_uuid.test(this.localGetParams.code)
      } else {
        this.isValidUUID = true
      }
    },

    selectUser(user) {
      this.$store.commit(SELECT_USER, user)
    },

    handleStatusChange(user) {
      this.selectUser(user)

      if (user.status === 'on') {
        eventBus.$emit('openModal', 'suspendUserConfirm')
      } else {
        this.$store.dispatch(SWITCH_USER, { id: this.selectedUserId })
      }
    },

    showModal(type, user_id) {
      let getUser = () => {
        if (!user_id) {
          return true
        }
        return this.$store
          .dispatch(GET_USER, { id: user_id })
          .then(data => {
            this.selectUser(data)
          })
      }

      let requirements = {
        changeDevices: [ getUser ],
        changeExpiration: [ getUser ],
        resetSessionsConfirm: [ getUser ],
        userToken: [
          getUser,
          () => this.$store.dispatch(GET_USER_TOKEN, { user_id })
        ],
        downloadConfig: [
          getUser,
          () => this.$store.dispatch(GET_USER_SERVERS, { user_id }),
          () => this.$store.dispatch(GET_USER_VPN_SERVERS, { user_id })
        ],
        userServers: [
          getUser,
          () => this.$store.dispatch(GET_USER_SERVERS, { user_id, shouldGroup: true })
        ],
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.dataIsReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },
  }
}
</script>
